import React, { useEffect, useState } from "react";
import { Box, Button, Chip, CircularProgress, TextField, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { useDispatch } from "react-redux";
import { fetchTicketStatus } from "../../store/actions/chatbot";
import dayjs from "dayjs";

const STATUS_COLOR = {
  open:        "warning",
  pending:     "error",
  in_progress: "info",
  resolved:    "success",
  closed:      "default",
};

function TicketStatusCard({ ticketId: initialTicketId, showSuccessBadge }) {
  const dispatch = useDispatch();
  const [inputId, setInputId]   = useState("");
  const [ticket, setTicket]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [showInput, setShowInput] = useState(!initialTicketId);

  // Auto-fetch when a ticket_id was extracted by the LLM
  useEffect(() => {
    if (initialTicketId) fetchTicket(initialTicketId);
  }, []);

  const fetchTicket = async (id) => {
    setLoading(true);
    setError("");
    setTicket(null);
    setShowInput(false);
    try {
      const data = await dispatch(fetchTicketStatus(id));
      setTicket(data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setError(
          `No ticket found with ID "${id}" in your account. ` +
          "Please check the ticket ID and try again."
        );
      } else {
        setError("Unable to fetch ticket details. Please try again.");
      }
      setShowInput(true); // Let user correct the ID on any error
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    const id = inputId.trim().toUpperCase();
    if (!id) { setError("Please enter a ticket ID."); return; }
    fetchTicket(id);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  if (loading) {
    return (
      <div className="ticket-status-card">
        <Box display="flex" alignItems="center" gap={1}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">Fetching ticket...</Typography>
        </Box>
      </div>
    );
  }

  // Show input form (initial state or after error)
  if (showInput) {
    return (
      <div className="ticket-status-card">
        <Typography variant="body2" fontWeight={600} mb={1}>Check Ticket Status</Typography>
        {error && (
          <Box display="flex" alignItems="flex-start" gap={0.5} mb={1}>
            <ErrorOutlineIcon color="error" sx={{ fontSize: 16, mt: "2px", flexShrink: 0 }} />
            <Typography variant="caption" color="error">{error}</Typography>
          </Box>
        )}
        <Box display="flex" gap={1}>
          <TextField
            size="small"
            placeholder="e.g. AYE-2026-00001"
            value={inputId}
            onChange={(e) => { setInputId(e.target.value); setError(""); }}
            onKeyDown={handleKeyDown}
            sx={{ flex: 1 }}
            autoFocus
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleSearch}
            sx={{ textTransform: "none", whiteSpace: "nowrap" }}
          >
            Check
          </Button>
        </Box>
      </div>
    );
  }

  if (!ticket) return null;

  return (
    <div className="ticket-status-card">
      {showSuccessBadge && (
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <CheckCircleOutlineIcon color="success" fontSize="small" />
          <Typography variant="caption" color="success.main" fontWeight={700}>
            Ticket raised successfully
          </Typography>
        </Box>
      )}
      <div className="ticket-status-card__id">{ticket.ticket_id}</div>
      <div className="ticket-status-card__subject">{ticket.subject}</div>
      <div className="ticket-status-card__row">
        <span>Status:</span>
        <Chip
          label={ticket.status.replace("_", " ")}
          size="small"
          color={STATUS_COLOR[ticket.status] || "default"}
        />
      </div>
      <div className="ticket-status-card__row">
        <span>Category:</span>
        <span style={{ textTransform: "capitalize" }}>{ticket.category}</span>
      </div>
      {ticket.assigned_to_name && (
        <div className="ticket-status-card__row">
          <span>Assigned to:</span>
          <span>{ticket.assigned_to_name}</span>
        </div>
      )}
      <div className="ticket-status-card__row">
        <span>Raised:</span>
        <span>{dayjs(ticket.created_on).format("DD MMM YYYY")}</span>
      </div>
      {ticket.resolved_on && (
        <div className="ticket-status-card__row">
          <span>Resolved:</span>
          <span>{dayjs(ticket.resolved_on).format("DD MMM YYYY")}</span>
        </div>
      )}
      {ticket.attachments && ticket.attachments.length > 0 && (
        <Box mt={1}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            {ticket.attachments.length} attachment{ticket.attachments.length > 1 ? "s" : ""}
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={0.75}>
            {ticket.attachments.map((att) => {
              const isImage = /\.(png|jpe?g|gif|webp|bmp)$/i.test(att.file_name || "");
              return (
                <a
                  key={att.id}
                  href={att.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "none" }}
                >
                  {isImage ? (
                    <img
                      src={att.url}
                      alt={att.file_name}
                      style={{
                        width: 56, height: 56, objectFit: "cover",
                        borderRadius: 6, border: "1px solid #e0e0e0",
                      }}
                    />
                  ) : (
                    <Box
                      display="flex" alignItems="center" gap={0.5}
                      sx={{
                        px: 1, py: 0.5, border: "1px solid #e0e0e0", borderRadius: 1,
                        fontSize: 12, color: "text.secondary", maxWidth: 140,
                      }}
                    >
                      <InsertDriveFileIcon sx={{ fontSize: 14 }} />
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {att.file_name}
                      </span>
                    </Box>
                  )}
                </a>
              );
            })}
          </Box>
        </Box>
      )}
    </div>
  );
}

export default TicketStatusCard;
